import { ref, onMounted, computed, watch, nextTick } from 'vue';
import Sidebar from '@/components/Sidebar.vue';
import Header from '@/components/HeaderPanel.vue';
import Swal from 'sweetalert2';
import { hasPermission } from '@/store/userStore';
import { RepuestoService } from '@/services/RepuestoService';
import { AtributoService } from '@/services/AtributoService';
import { DesplegableService } from '@/services/DesplegableService';
import { DepartamentoService } from '@/services/DepartamentoService';
import { CategoriaService } from '@/services/CategoriaService';
import { PERMISSIONS } from '@/constants/permissions';
import Sortable from 'sortablejs';
import { PlusIcon, EditIcon, TrashIcon, ListIcon, ChevronLeftIcon, ChevronRightIcon, XIcon, PackageIcon, TagIcon, LayersIcon, GridIcon, SearchIcon, MoveIcon, DownloadIcon, InfoIcon, GripVerticalIcon, FileTextIcon, PowerIcon, Loader2Icon } from 'lucide-vue-next';
const Toast = Swal.mixin({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
    didOpen: (toast) => {
        toast.addEventListener('mouseenter', Swal.stopTimer);
        toast.addEventListener('mouseleave', Swal.resumeTimer);
    }
});
import draggable from 'vuedraggable';
export default {
    name: 'Repuestos',
    components: {
        Sidebar, Header,
        PlusIcon, EditIcon, TrashIcon, ListIcon,
        ChevronLeftIcon, ChevronRightIcon, XIcon,
        PackageIcon, TagIcon, LayersIcon, GridIcon, SearchIcon, MoveIcon, DownloadIcon, InfoIcon, GripVerticalIcon, FileTextIcon, PowerIcon, Loader2Icon,
        draggable
    },
    setup() {
        const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
        const repuestos = ref([]);
        const isLoading = ref(false);
        const searchQuery = ref('');
        const selectedLetter = ref('');
        const currentPage = ref(1);
        const itemsPerPage = ref(8);
        const isModalOpen = ref(false);
        const isEditing = ref(false);
        const isSaving = ref(false);
        const creationMode = ref('individual');
        const bulkNombresList = ref(['']);
        const addBulkInput = () => bulkNombresList.value.push('');
        const removeBulkInput = (index) => bulkNombresList.value.splice(index, 1);
        const currentForm = ref({ repuestoID: 0, nombre: '', departamentoID: 0, categoriaID: 0, active: true });
        const isModalOpenDesplegable = ref(false);
        const isExportModalOpen = ref(false);
        const isExporting = ref(false);
        const availableColumns = ref([
            { id: 1, name: "Nombre" },
            { id: 2, name: "Departamento" },
            { id: 3, name: "Categoría" }
        ]);
        const exportConfig = ref({
            format: 'excel',
            filterType: 'todos',
            selectedColumns: ["Nombre", "Departamento", "Categoría"],
            sortColumn: "Nombre",
            sortDirection: "asc"
        });
        const departamentos = ref([]);
        const todasCategorias = ref([]);
        const categoriasFiltradas = ref([]);
        const currentRepuesto = ref(null);
        const desplegablesList = ref([]);
        const allDesplegables = ref([]);
        const searchDesplegables = ref("");
        const selectedDesplegableToAdd = ref(null);
        const filterDepartamentoId = ref(null);
        const filterCategoriaId = ref(null);
        const totalPages = ref(1);
        const totalRepuestosCount = ref(0);
        const loadRepuestos = async () => {
            isLoading.value = true;
            try {
                const res = await RepuestoService.getPaginatedRepuestos(currentPage.value, searchQuery.value, selectedLetter.value || "", filterDepartamentoId.value, filterCategoriaId.value);
                repuestos.value = res.items || res.Items || [];
                totalRepuestosCount.value = res.totalCount || res.TotalCount || 0;
                totalPages.value = res.totalPages || res.TotalPages || 1;
            }
            catch (e) {
                console.error(e);
            }
            finally {
                isLoading.value = false;
            }
        };
        const loadDepartamentos = async () => {
            try {
                departamentos.value = await DepartamentoService.getAll();
            }
            catch (e) {
                console.error(e);
            }
        };
        const loadCategorias = async () => {
            try {
                todasCategorias.value = await CategoriaService.getAll();
            }
            catch (e) {
                console.error(e);
            }
        };
        const searchDepartamento = ref('');
        const showDepartamentos = ref(false);
        const searchCategoria = ref('');
        const showCategorias = ref(false);
        const filteredDepartamentosList = computed(() => {
            let deps = departamentos.value;
            if (searchDepartamento.value) {
                const q = searchDepartamento.value.toLowerCase();
                deps = deps.filter(d => d.nombre.toLowerCase().includes(q));
            }
            return deps.map(d => {
                if (!searchDepartamento.value)
                    return { ...d, highlightedNombre: d.nombre };
                const regex = new RegExp(`(${searchDepartamento.value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
                const highlightedNombre = d.nombre.replace(regex, `<mark>$1</mark>`);
                return { ...d, highlightedNombre };
            });
        });
        const filteredCategoriasList = computed(() => {
            let cats = categoriasFiltradas.value;
            if (searchCategoria.value) {
                const q = searchCategoria.value.toLowerCase();
                cats = cats.filter(c => c.nombre.toLowerCase().includes(q));
            }
            return cats.map(c => {
                if (!searchCategoria.value)
                    return { ...c, highlightedNombre: c.nombre };
                const regex = new RegExp(`(${searchCategoria.value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
                const highlightedNombre = c.nombre.replace(regex, `<mark>$1</mark>`);
                return { ...c, highlightedNombre };
            });
        });
        const searchFilterDep = ref('');
        const showFilterDeps = ref(false);
        const searchFilterCat = ref('');
        const showFilterCats = ref(false);
        const filteredFilterDepsList = computed(() => {
            let deps = departamentos.value;
            if (searchFilterDep.value) {
                const q = searchFilterDep.value.toLowerCase();
                deps = deps.filter(d => d.nombre.toLowerCase().includes(q));
            }
            return deps.map(d => {
                if (!searchFilterDep.value)
                    return { ...d, highlightedNombre: d.nombre };
                const regex = new RegExp(`(${searchFilterDep.value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
                const highlightedNombre = d.nombre.replace(regex, `<mark>$1</mark>`);
                return { ...d, highlightedNombre };
            });
        });
        const filteredFilterCatsList = computed(() => {
            if (!filterDepartamentoId.value)
                return [];
            let cats = todasCategorias.value.filter(c => c.departamentoID === filterDepartamentoId.value && c.estado !== false);
            if (searchFilterCat.value) {
                const q = searchFilterCat.value.toLowerCase();
                cats = cats.filter(c => c.nombre.toLowerCase().includes(q));
            }
            return cats.map(c => {
                if (!searchFilterCat.value)
                    return { ...c, highlightedNombre: c.nombre };
                const regex = new RegExp(`(${searchFilterCat.value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
                const highlightedNombre = c.nombre.replace(regex, `<mark>$1</mark>`);
                return { ...c, highlightedNombre };
            });
        });
        const selectFilterDep = (dep) => {
            filterDepartamentoId.value = dep.departamentoID;
            searchFilterDep.value = dep.nombre;
            showFilterDeps.value = false;
            onFilterDepartamentoChange();
        };
        const clearFilterDep = () => {
            filterDepartamentoId.value = null;
            searchFilterDep.value = '';
            showFilterDeps.value = false;
            onFilterDepartamentoChange();
        };
        const onBlurFilterDep = () => {
            setTimeout(() => {
                showFilterDeps.value = false;
                if (searchFilterDep.value.trim() === '') {
                    filterDepartamentoId.value = null;
                    onFilterDepartamentoChange();
                }
                else {
                    const selected = departamentos.value.find(d => d.departamentoID === filterDepartamentoId.value);
                    if (selected) {
                        searchFilterDep.value = selected.nombre;
                    }
                    else {
                        searchFilterDep.value = '';
                        filterDepartamentoId.value = null;
                        onFilterDepartamentoChange();
                    }
                }
            }, 150);
        };
        const selectFilterCat = (cat) => {
            filterCategoriaId.value = cat.categoriaID;
            searchFilterCat.value = cat.nombre;
            showFilterCats.value = false;
        };
        const clearFilterCat = () => {
            filterCategoriaId.value = null;
            searchFilterCat.value = '';
            showFilterCats.value = false;
        };
        const onBlurFilterCat = () => {
            setTimeout(() => {
                showFilterCats.value = false;
                if (searchFilterCat.value.trim() === '') {
                    filterCategoriaId.value = null;
                }
                else {
                    const selected = todasCategorias.value.find(c => c.categoriaID === filterCategoriaId.value);
                    if (selected) {
                        searchFilterCat.value = selected.nombre;
                    }
                    else {
                        searchFilterCat.value = '';
                        filterCategoriaId.value = null;
                    }
                }
            }, 150);
        };
        const onFilterDepartamentoChange = () => {
            filterCategoriaId.value = null;
            searchFilterCat.value = '';
            loadRepuestos();
        };
        const onDepartamentoChange = async () => {
            currentForm.value.categoriaID = 0;
            searchCategoria.value = '';
            const id = currentForm.value.departamentoID;
            if (id) {
                categoriasFiltradas.value = todasCategorias.value.filter(c => c.departamentoID === id && c.estado !== false);
            }
            else {
                categoriasFiltradas.value = [];
            }
        };
        const selectDepartamento = (dep) => {
            currentForm.value.departamentoID = dep.departamentoID;
            searchDepartamento.value = dep.nombre;
            showDepartamentos.value = false;
            onDepartamentoChange();
        };
        const selectCategoria = (cat) => {
            currentForm.value.categoriaID = cat.categoriaID;
            searchCategoria.value = cat.nombre;
            showCategorias.value = false;
        };
        const onBlurDepartamento = () => {
            setTimeout(() => {
                showDepartamentos.value = false;
                const selected = departamentos.value.find(d => d.departamentoID === currentForm.value.departamentoID);
                if (selected) {
                    searchDepartamento.value = selected.nombre;
                }
                else {
                    searchDepartamento.value = '';
                    currentForm.value.departamentoID = 0;
                    onDepartamentoChange();
                }
            }, 150);
        };
        const onBlurCategoria = () => {
            setTimeout(() => {
                showCategorias.value = false;
                const selected = categoriasFiltradas.value.find(c => c.categoriaID === currentForm.value.categoriaID);
                if (selected) {
                    searchCategoria.value = selected.nombre;
                }
                else {
                    searchCategoria.value = '';
                    currentForm.value.categoriaID = 0;
                }
            }, 150);
        };
        const getNombreDepartamento = (id) => departamentos.value.find(d => d.departamentoID === id)?.nombre ?? '—';
        const getNombreCategoria = (id) => todasCategorias.value.find(c => c.categoriaID === id)?.nombre ?? '—';
        let debounceTimeout = null;
        watch([searchQuery, selectedLetter, filterDepartamentoId, filterCategoriaId], () => {
            currentPage.value = 1;
            clearTimeout(debounceTimeout);
            debounceTimeout = setTimeout(() => {
                loadRepuestos();
            }, 300);
        });
        watch(searchFilterDep, (newVal) => {
            if (newVal.trim() === '' && filterDepartamentoId.value !== null) {
                filterDepartamentoId.value = null;
                filterCategoriaId.value = null;
                searchFilterCat.value = '';
            }
        });
        watch(searchFilterCat, (newVal) => {
            if (newVal.trim() === '' && filterCategoriaId.value !== null) {
                filterCategoriaId.value = null;
            }
        });
        const paginatedRepuestos = computed(() => repuestos.value);
        const openModal = () => {
            isEditing.value = false;
            creationMode.value = 'individual';
            bulkNombresList.value = [''];
            currentForm.value = { repuestoID: 0, nombre: '', departamentoID: 0, categoriaID: 0, active: true };
            categoriasFiltradas.value = [];
            searchDepartamento.value = '';
            searchCategoria.value = '';
            isModalOpen.value = true;
        };
        const editRepuesto = async (item) => {
            isEditing.value = true;
            currentForm.value = { repuestoID: item.repuestoID, nombre: item.nombre, departamentoID: item.departamentoID, categoriaID: item.categoriaID, active: item.active !== false };
            categoriasFiltradas.value = todasCategorias.value.filter(c => c.departamentoID === item.departamentoID && (c.active !== false || c.categoriaID === item.categoriaID));
            searchDepartamento.value = getNombreDepartamento(item.departamentoID);
            searchCategoria.value = getNombreCategoria(item.categoriaID);
            isModalOpen.value = true;
        };
        const closeModal = () => isModalOpen.value = false;
        const saveRepuesto = async () => {
            if (creationMode.value === 'individual') {
                if (!currentForm.value.nombre.trim()) {
                    Swal.fire('Atención', 'El nombre del repuesto es obligatorio.', 'warning');
                    return;
                }
            }
            else {
                const hasValidBulk = bulkNombresList.value.some(n => n.trim() !== '');
                if (!hasValidBulk) {
                    Swal.fire('Atención', 'Debe ingresar al menos un nombre de repuesto.', 'warning');
                    return;
                }
            }
            if (!currentForm.value.departamentoID || currentForm.value.departamentoID === 0) {
                Swal.fire('Atención', 'Debe seleccionar un departamento.', 'warning');
                return;
            }
            if (!currentForm.value.categoriaID || currentForm.value.categoriaID === 0) {
                Swal.fire('Atención', 'Debe seleccionar una categoría.', 'warning');
                return;
            }
            try {
                isSaving.value = true;
                if (isEditing.value) {
                    const payload = { ...currentForm.value, fechaCreacion: new Date().toISOString() };
                    await RepuestoService.updateRepuesto(currentForm.value.repuestoID, payload);
                    Toast.fire({ icon: 'success', title: 'Repuesto actualizado correctamente' });
                }
                else {
                    if (creationMode.value === 'individual') {
                        const payload = { ...currentForm.value, fechaCreacion: new Date().toISOString() };
                        await RepuestoService.addRepuesto(payload);
                        Toast.fire({ icon: 'success', title: 'Nuevo repuesto creado' });
                    }
                    else {
                        const nombres = bulkNombresList.value.map(n => n.trim()).filter(n => n);
                        if (nombres.length === 0) {
                            Swal.fire('Atención', 'Debe ingresar al menos un nombre de repuesto válido.', 'warning');
                            return;
                        }
                        await RepuestoService.addRepuestoBulk({
                            departamentoID: currentForm.value.departamentoID,
                            categoriaID: currentForm.value.categoriaID,
                            nombres
                        });
                        Toast.fire({ icon: 'success', title: 'Nuevos repuestos creados' });
                    }
                }
                closeModal();
                loadRepuestos();
            }
            catch (e) {
                Swal.fire('Error', e.message || 'No se pudo guardar', 'error');
            }
            finally {
                isSaving.value = false;
            }
        };
        const confirmDeleteRepuesto = async (id) => {
            const res = await Swal.fire({ title: '¿Eliminar Repuesto?', text: 'Se perderá la configuración de sus desplegables.', icon: 'warning', showCancelButton: true });
            if (res.isConfirmed) {
                try {
                    await RepuestoService.deleteRepuesto(id);
                    loadRepuestos();
                }
                catch (e) {
                    Swal.fire({
                        title: 'No se puede eliminar',
                        text: e?.message || 'No se pudo eliminar el repuesto.',
                        icon: 'error',
                        confirmButtonText: 'Entendido'
                    });
                }
            }
        };
        const toggleActive = async (item) => {
            const result = await Swal.fire({
                title: '¿Cambiar estado?',
                text: `¿Estás seguro de que deseas ${item.active ? 'desactivar' : 'activar'} el repuesto "${item.nombre}"?`,
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: 'Sí, cambiar',
                cancelButtonText: 'Cancelar',
                confirmButtonColor: '#3b82f6',
                cancelButtonColor: '#94a3b8',
            });
            if (!result.isConfirmed)
                return;
            try {
                await RepuestoService.toggleActivo(item.repuestoID);
                item.active = !item.active;
                Toast.fire({
                    title: item.active ? 'Habilitado' : 'Deshabilitado',
                    text: `El repuesto ahora está ${item.active ? 'activo' : 'inactivo'}.`,
                    icon: 'success'
                });
            }
            catch (err) {
                Swal.fire({ title: 'Error', text: err.message || 'No se pudo cambiar el estado', icon: 'error' });
            }
        };
        const openDesplegablesModal = async (item) => {
            currentRepuesto.value = item;
            searchDesplegables.value = "";
            selectedDesplegableToAdd.value = null;
            await fetchDesplegablesList(item.repuestoID);
            if (allDesplegables.value.length === 0) {
                allDesplegables.value = await DesplegableService.getAllDesplegables();
            }
            isModalOpenDesplegable.value = true;
            nextTick(() => initSortable());
        };
        const fetchDesplegablesList = async (id) => {
            const res = await AtributoService.getAtributosByRepuestoId(id);
            desplegablesList.value = (res || []).sort((a, b) => a.orden - b.orden);
        };
        const closeDesplegableModal = () => isModalOpenDesplegable.value = false;
        const filteredDesplegables = computed(() => {
            if (searchDesplegables.value.length < 2)
                return [];
            const linkedIds = desplegablesList.value.map(d => d.nombreAtributo.nombreAtributoID);
            return allDesplegables.value
                .filter(d => !linkedIds.includes(d.nombreAtributoID))
                .filter(d => d.nombreAtributo.toLowerCase().includes(searchDesplegables.value.toLowerCase()))
                .slice(0, 6);
        });
        const selectDesplegableToAdd = (item) => {
            searchDesplegables.value = item.nombreAtributo;
            selectedDesplegableToAdd.value = item;
        };
        const addDesplegableToRepuesto = async () => {
            if (!selectedDesplegableToAdd.value)
                return;
            try {
                await AtributoService.addAtributo({
                    atributoID: 0,
                    repuestoID: currentRepuesto.value.repuestoID,
                    nombreAtributoID: selectedDesplegableToAdd.value.nombreAtributoID,
                    orden: desplegablesList.value.length + 1
                });
                searchDesplegables.value = "";
                selectedDesplegableToAdd.value = null;
                await fetchDesplegablesList(currentRepuesto.value.repuestoID);
                Toast.fire({ icon: 'success', title: 'Atributo vinculado correctamente' });
            }
            catch (e) {
                Swal.fire('Error', 'No se pudo vincular', 'error');
            }
        };
        const confirmDeleteDesplegableItem = async (id) => {
            const res = await Swal.fire({ title: '¿Desvincular?', icon: 'warning', showCancelButton: true });
            if (res.isConfirmed) {
                try {
                    await AtributoService.deleteAtributo(id);
                    await fetchDesplegablesList(currentRepuesto.value.repuestoID);
                    Toast.fire({ icon: 'success', title: 'Atributo desvinculado correctamente' });
                }
                catch (e) {
                    Swal.fire('Error', 'No se pudo desvincular el atributo', 'error');
                }
            }
        };
        const initSortable = () => {
            const el = document.getElementById('sortable');
            if (!el)
                return;
            Sortable.create(el, {
                animation: 150,
                handle: '.drag-handle',
                onEnd: async (evt) => {
                    if (evt.oldIndex === undefined || evt.newIndex === undefined)
                        return;
                    const moved = desplegablesList.value.splice(evt.oldIndex, 1)[0];
                    desplegablesList.value.splice(evt.newIndex, 0, moved);
                    for (const [index, item] of desplegablesList.value.entries()) {
                        if (item.orden !== index + 1)
                            await AtributoService.updateOrdenAtributo(item.atributoID, index + 1);
                    }
                }
            });
        };
        const toggleColumnSelection = (columnName) => {
            const index = exportConfig.value.selectedColumns.indexOf(columnName);
            if (index > -1) {
                if (exportConfig.value.selectedColumns.length > 1) { // Ensure at least one column remains selected
                    exportConfig.value.selectedColumns.splice(index, 1);
                }
                else {
                    Swal.fire('Advertencia', 'Debe seleccionar al menos una columna para exportar.', 'warning');
                }
            }
            else {
                exportConfig.value.selectedColumns.push(columnName);
            }
        };
        const filterByLetter = (letter) => {
            selectedLetter.value = letter;
            searchQuery.value = '';
        };
        const resetFilter = () => {
            selectedLetter.value = '';
            searchQuery.value = '';
            filterDepartamentoId.value = null;
            filterCategoriaId.value = null;
            searchFilterDep.value = '';
            searchFilterCat.value = '';
        };
        const prevPage = () => {
            if (currentPage.value > 1) {
                currentPage.value--;
                loadRepuestos();
            }
        };
        const nextPage = () => {
            if (currentPage.value < totalPages.value) {
                currentPage.value++;
                loadRepuestos();
            }
        };
        onMounted(async () => { await Promise.all([loadRepuestos(), loadDepartamentos(), loadCategorias()]); });
        return {
            P: PERMISSIONS,
            can: hasPermission,
            alphabet,
            hasPermission,
            repuestos, isLoading, paginatedRepuestos, searchQuery, selectedLetter, currentPage, totalPages, totalRepuestosCount,
            isModalOpen, isEditing, isSaving, currentForm, isModalOpenDesplegable, currentRepuesto, desplegablesList,
            creationMode, bulkNombresList, addBulkInput, removeBulkInput,
            searchDesplegables, filteredDesplegables, selectedDesplegableToAdd,
            departamentos, categoriasFiltradas,
            resetFilter, prevPage, nextPage, openModal, closeModal, saveRepuesto, confirmDeleteRepuesto, editRepuesto, toggleActive,
            openDesplegablesModal, closeDesplegableModal, selectDesplegableToAdd, addDesplegableToRepuesto, confirmDeleteDesplegableItem,
            onFilterDepartamentoChange, getNombreDepartamento, getNombreCategoria,
            searchDepartamento, showDepartamentos, searchCategoria, showCategorias, filteredDepartamentosList, filteredCategoriasList, selectDepartamento, onBlurDepartamento, selectCategoria, onBlurCategoria,
            filterDepartamentoId, filterCategoriaId, filterByLetter,
            searchFilterDep, showFilterDeps, filteredFilterDepsList, selectFilterDep, clearFilterDep, onBlurFilterDep,
            searchFilterCat, showFilterCats, filteredFilterCatsList, selectFilterCat, clearFilterCat, onBlurFilterCat,
            isExportModalOpen, isExporting, availableColumns, exportConfig,
            openExportModal: () => {
                isExportModalOpen.value = true;
            },
            closeExportModal: () => isExportModalOpen.value = false,
            handleExport: async () => {
                if (exportConfig.value.selectedColumns.length === 0) {
                    Swal.fire('Advertencia', 'Debe seleccionar al menos una columna para exportar.', 'warning');
                    return;
                }
                isExporting.value = true;
                try {
                    const orderedColumns = availableColumns.value
                        .filter(col => exportConfig.value.selectedColumns.includes(col.name))
                        .map(col => col.name);
                    const blob = await RepuestoService.exportReport({
                        format: exportConfig.value.format,
                        selectedColumns: orderedColumns,
                        search: searchQuery.value,
                        letter: selectedLetter.value || undefined,
                        departamentoId: filterDepartamentoId.value,
                        categoriaId: filterCategoriaId.value,
                        sortColumn: exportConfig.value.sortColumn,
                        sortDirection: exportConfig.value.sortDirection
                    });
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', `Reporte_Repuestos_${new Date().getTime()}.${exportConfig.value.format === 'excel' ? 'xlsx' : 'pdf'}`);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    isExportModalOpen.value = false;
                    Toast.fire({ icon: 'success', title: 'Reporte generado correctamente' });
                }
                catch (e) {
                    console.error("Error exporting report:", e);
                    Swal.fire('Error', 'No se pudo generar el reporte', 'error');
                }
                finally {
                    isExporting.value = false;
                }
            },
            toggleColumnSelection,
            searchDesplegable: () => { }
        };
    }
};
